import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { api, extractErr } from "@/portal/PortalAuth";
import { btn, ghost, inp } from "@/portal/PortalShell";

const SCHEDULES = [["off", "Manual only"], ["6h", "Every 6 hours"], ["12h", "Every 12 hours"], ["24h", "Daily"]];

/** Verify -> preview -> confirm flow for connecting a Masaya listing to its Airbnb calendar (read-only iCal import). */
export function AirbnbConnect({ room, onChanged }) {
  const [replacing, setReplacing] = useState(false);
  const s = room.slug;
  return (
    <div data-testid={`airbnb-connect-${s}`}>
      {room.ical_sync_enabled && <ConnectionStatus room={room} onChanged={onChanged} onReplace={() => setReplacing((v) => !v)} />}
      {(!room.ical_sync_enabled || replacing) && <VerifyForm room={room} onDone={() => { setReplacing(false); onChanged(); }} />}
    </div>
  );
}

function useBusy() {
  const [busy, setBusy] = useState("");
  const run = async (key, fn) => {
    setBusy(key);
    try { return await fn(); } catch (e) { toast.error(extractErr(e)); return null; } finally { setBusy(""); }
  };
  return [busy, run];
}

function ConnectionStatus({ room, onChanged, onReplace }) {
  const [busy, run] = useBusy();
  const s = room.slug;
  const failed = room.ical_last_status === "failed";
  const sync = () => run("sync", async () => {
    const { data } = await api.post(`/admin/booking/rooms/${room.id}/sync-airbnb`);
    toast.success(`Imported ${data.events_imported} Airbnb block(s).`); onChanged();
  });
  const disconnect = () => window.confirm("Stop syncing this listing? Imported Airbnb blocks stay in place so nothing gets double-booked.") &&
    run("disconnect", async () => { await api.post(`/admin/booking/rooms/${room.id}/ical/disconnect`); toast.success("Sync disabled."); onChanged(); });
  const setSchedule = (sync_schedule) => run("schedule", async () => {
    await api.patch(`/admin/booking/rooms/${room.id}`, { name: room.name, slug: room.slug, property_id: room.property_id, sync_schedule });
    toast.success("Sync schedule saved."); onChanged();
  });
  return (
    <div className="mt-3 rounded-xl border border-[#E7E5E4] p-3 space-y-2 text-xs" data-testid={`sync-connected-${s}`}>
      <p className="inline-flex items-center gap-1 text-[#4A6A48]"><ShieldCheck className="w-3.5 h-3.5" /> Connected · Airbnb listing {room.ical_verified_listing_id} · verified by {room.ical_verified_by}</p>
      <p className="text-[#44403C]" data-testid={`sync-last-${s}`}>Last sync: {room.ical_last_sync_at ? new Date(room.ical_last_sync_at).toLocaleString() : "never"} · Status: <span className={failed ? "text-red-700" : "text-[#4A6A48]"}>{room.ical_last_status}</span></p>
      {room.airbnb_ical_connected_url && <p className="text-[#78716C] break-all">Calendar: {room.airbnb_ical_connected_url}</p>}
      {failed && <p data-testid={`sync-error-${s}`} className="text-red-700 inline-flex gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Last sync failed: {room.ical_last_error || "unknown error"}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={sync} disabled={!!busy} className={btn + " text-xs"} data-testid={`sync-run-${s}`}>
          {busy === "sync" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync now
        </button>
        <button onClick={onReplace} className={ghost + " text-xs"} data-testid={`sync-replace-${s}`}>Replace calendar URL</button>
        <button onClick={disconnect} disabled={!!busy} className={ghost + " text-xs"} data-testid={`sync-disconnect-${s}`}>Disconnect</button>
        <label className="ml-auto text-[#78716C]">Scheduled sync
          <select value={room.sync_schedule || "off"} onChange={(e) => setSchedule(e.target.value)} data-testid={`sync-schedule-${s}`} className={inp + " h-8 mt-1 text-xs"}>
            {SCHEDULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <p className="text-[11px] text-[#78716C]">Scheduled sync runs automatically (checked every 30 minutes) for verified, connected listings. Choose "Manual only" to pause it.</p>
    </div>
  );
}

function VerifyForm({ room, onDone }) {
  const [url, setUrl] = useState("");
  const [listingId, setListingId] = useState(room.airbnb_listing_id || "");
  const [preview, setPreview] = useState(null);
  const [busy, run] = useBusy();
  const s = room.slug;
  const payload = { url, airbnb_listing_id: listingId };
  const verify = () => run("verify", async () => {
    const { data } = await api.post(`/admin/booking/rooms/${room.id}/ical/verify`, payload);
    setPreview(data);
  });
  const connect = () => {
    if (!window.confirm(`Connect ${room.name} to Airbnb listing ${preview.listing_id_in_url} and import ${preview.event_count} blocked period(s)? Nothing is changed on Airbnb.`)) return;
    run("connect", async () => {
      const { data } = await api.post(`/admin/booking/rooms/${room.id}/ical/connect`, { ...payload, confirm: true });
      toast.success(`Connected. Imported ${data.events_imported} Airbnb block(s).`);
      setPreview(null); setUrl(""); onDone();
    });
  };
  const reset = (fn) => (e) => { fn(e.target.value); setPreview(null); };
  return (
    <div className="mt-3 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-2">
        <input placeholder="Airbnb listing ID" value={listingId} disabled={Boolean(room.airbnb_listing_id)} onChange={reset(setListingId)} data-testid={`sync-listing-id-${s}`} className={inp} />
        <input placeholder="Private Airbnb calendar export link (.ics)" value={url} onChange={reset(setUrl)} data-testid={`sync-url-${s}`} className={inp} />
        <button onClick={verify} disabled={!!busy || !url || !listingId} className={ghost + " text-xs"} data-testid={`sync-verify-${s}`}>
          {busy === "verify" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Verify (no import)
        </button>
      </div>
      {preview && (
        <div className="rounded-xl bg-[#FAFAF9] border border-[#E7E5E4] p-3 text-xs space-y-2" data-testid={`sync-preview-${s}`}>
          <p className="text-[#4A6A48] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Link matches {preview.room.name} · Airbnb listing {preview.listing_id_in_url}</p>
          <p className="text-[#44403C]">{preview.event_count} blocked period(s) found. Upcoming:</p>
          <ul className="max-h-40 overflow-y-auto space-y-0.5 text-[#1C1917]" data-testid={`sync-preview-events-${s}`}>
            {(preview.upcoming_events || []).map((e) => <li key={e.uid}>{e.start} → {e.end} · {e.summary}</li>)}
            {(preview.upcoming_events || []).length === 0 && <li className="text-[#78716C]">No upcoming blocked dates.</li>}
          </ul>
          {(preview.direct_reservation_conflicts || []).length > 0 && (
            <p className="text-red-700" data-testid={`sync-preview-conflicts-${s}`}>{preview.direct_reservation_conflicts.length} direct reservation(s) overlap these Airbnb dates. Review them before connecting.</p>
          )}
          <button onClick={connect} disabled={!!busy} className={btn + " text-xs"} data-testid={`sync-connect-${s}`}>
            {busy === "connect" ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Confirm & connect calendar
          </button>
        </div>
      )}
    </div>
  );
}
